/**
 * Usage reporting — generated by OneAdmin. Safe to delete; the app works without it.
 *
 * Tells OneAdmin who is using this app, so an app whose visitors never sign up is still visible
 * to whoever runs it — and so the ones who DO sign in appear as subscribers rather than as
 * anonymous devices.
 *
 * It sends a device id this browser keeps, plus the signed-in user's id and email when it can
 * find them: it looks for the session this app already keeps (supabase-js or Firebase, both in
 * localStorage) and reads ONLY the id and the email out of it. The access and refresh tokens in
 * that same object are never read and never leave the browser. If this app signs people in some
 * other way, call identifyUsage({ user, email }) yourself.
 *
 * It never sends an address: the server reads that from the request.
 */
const APP_SLUG = "pcakefresh";
const USAGE_KEY = "usg_cfd154558b5064b84f414efbd77cef0dc89122d1286baa73";
const ENDPOINT = "https://anewoiyqrskohxkvbpfr.supabase.co/functions/v1/app-usage";
const DEVICE_STORAGE_KEY = "oneadmin_usage_device_v1";
/** One ping per 5 minutes per page. A visibility change on a phone fires constantly. */
const PING_EVERY_MS = 5 * 60 * 1000;
/** How often to look again for a sign-in that happened in THIS tab. A storage event does not
 *  fire in the tab that wrote it, so a timer is the only way to notice your own login. Cheap:
 *  it reads localStorage and sends nothing unless the identity actually changed. */
const WATCH_EVERY_MS = 20 * 1000;

/** A per-browser id. Not a fingerprint — clearing site data yields a new one, on purpose. */
function deviceId() {
  try {
    let id = localStorage.getItem(DEVICE_STORAGE_KEY);
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID()) ||
        String(Date.now()) + Math.random().toString(16).slice(2);
      localStorage.setItem(DEVICE_STORAGE_KEY, id);
    }
    return id;
  } catch {
    // Private mode, or storage blocked. Report as a one-off device rather than not at all.
    return "ephemeral-" + Math.random().toString(16).slice(2);
  }
}

/** supabase-js stores its session as JSON; newer versions prefix it with "base64-". */
function parseStored(raw) {
  try {
    let s = String(raw || "");
    // globalThis.atob, not a bare atob: the generated file lands in someone else's project and
    // their linter decides what counts as a global. Same function, no "'atob' is not defined".
    if (s.slice(0, 7) === "base64-") s = globalThis.atob(s.slice(7));
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/**
 * Who is signed in, according to the session this app already keeps.
 *
 * Deliberately narrow: the id and the email, and nothing else. Returns null when nobody is
 * signed in, when storage is blocked, or when the app's login is one this cannot read — all of
 * which simply mean the device is reported without an account attached.
 */
function detectIdentity() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      // supabase-js: sb-<project-ref>-auth-token
      if (k.slice(0, 3) === "sb-" && k.indexOf("-auth-token") > 0) {
        const v = parseStored(localStorage.getItem(k));
        const u = v && (v.user || (v.currentSession && v.currentSession.user) || (v.session && v.session.user));
        if (u && u.id) return { user: String(u.id), email: u.email ? String(u.email) : undefined };
      }
      // Firebase: firebase:authUser:<apiKey>:<name>
      if (k.slice(0, 18) === "firebase:authUser:") {
        const v = parseStored(localStorage.getItem(k));
        if (v && v.uid) return { user: String(v.uid), email: v.email ? String(v.email) : undefined };
      }
    }
  } catch {
    // Storage blocked — report the device and nothing else.
  }
  return null;
}

let lastPing = 0;
let lastIdentity = "";
let pinned = null;      // set by identifyUsage(), for logins this module cannot read
let watching = false;

/** Report this device. Quietly does nothing on failure — telemetry must never break the app. */
export async function reportUsage(opts) {
  if (opts && (opts.user || opts.email)) pinned = { user: opts.user, email: opts.email };
  const who = pinned || detectIdentity();
  const sig = who ? String(who.user || "") + "|" + String(who.email || "") : "";
  const now = Date.now();
  // A sign-in is news: report it at once rather than up to five minutes later, because the whole
  // point is that the person shows up as a subscriber while they are still here.
  const isNewIdentity = sig !== "" && sig !== lastIdentity;
  if (!(opts && opts.force) && !isNewIdentity && now - lastPing < PING_EVERY_MS) return;
  lastPing = now;
  if (sig) lastIdentity = sig;
  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        app: APP_SLUG,
        key: USAGE_KEY,
        device: deviceId(),
        user: (who && who.user) || undefined,
        email: (who && who.email) || undefined,
      }),
      keepalive: true,
    });
  } catch {
    /* offline, blocked, or the function is down — nothing to do and nothing to say */
  }
}

/**
 * Name the signed-in user yourself, and report straight away.
 *
 * Only needed when this app's login is neither supabase-js nor Firebase — those are found on
 * their own. Safe to call on every auth change; it is the app saying who is here, so it wins
 * over what the module can detect.
 */
export function identifyUsage(who) {
  return reportUsage({ user: who && who.user, email: who && who.email, force: true });
}

/** Ping now, when a hidden tab comes back, and whenever the signed-in user changes. Call once,
 *  as early as the app likes — no arguments needed. */
export function startUsageReporting(opts) {
  reportUsage(opts);
  if (watching) return;
  watching = true;
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) reportUsage();
    });
  }
  if (typeof window !== "undefined") {
    // A sign-in in ANOTHER tab writes the session, which fires this one.
    window.addEventListener("storage", () => reportUsage());
    // A sign-in in THIS tab fires nothing, so look again on a timer.
    setInterval(() => reportUsage(), WATCH_EVERY_MS);
  }
}
